import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { useStoresUI } from '@context/StoresContext';
import { usePdp } from '@context/PdpContext';
import useDeliveryStockLevel from '@utils/delivery/useDeliveryStockLevel';
import SelectVariantMessage from '../StoreAvailability/SelectVariantMessage';
import SelectStoreMessage from '../StoreAvailability/SelectStoreMessage';
import { useHasMounted } from '@hooks/useHasMounted/useHasMounted';

import IconWrapper from '@components/ui/Icons';
import StoreAvailability from '@components/product/ProductBuyZone/MarketPlacerRedesign/StoreAvailability';
import ShippingRestrictionMessage from '@components/product/ProductBuyZone/MarketPlacerRedesign/ShippingRestrictionMessage';
import InStorePromotion from '@components/product/ProductBuyZone/InStorePromoMessage';
import { getPDPShippingOptions } from '@utils/shipping/getPDPShippingOptions';
import { ShippingRestrictions } from '@adapters/bigcommerce/types/product';
import { useLocale } from '@hooks/useLocale';
import styles from './ProductAvailability.module.scss';

const ProductAvailability = () => {
  const { t } = useTranslation('pdp');
  const locale = useLocale();
  const {
    selectedVariant,
    colours,
    sizes,
    shippingRestrictions,
    selectedAvailability,
  } = usePdp();
  const { favouriteStoreDetails } = useStoresUI();
  const isClient = useHasMounted();
  const stockLevelResult = useDeliveryStockLevel();
  const { stockLevel, stockLevelMessage } = stockLevelResult;
  const shippingOption =
    getPDPShippingOptions(
      shippingRestrictions as ShippingRestrictions,
      locale ?? 'en'
    ) ?? null;

  return (
    <div className={styles.fullfillmentMethodsContainer}>
      <div className={styles.storeFullfillmentOption}>
        <IconWrapper icon="pickup-store" width={24} height={24} />
        <div className={styles.fullfillmentOptionContent}>
          <div className={styles.fullfillmentOptionContentInnerContainer}>
            <p className={styles.fullfillmentOptionTitle}>
              {t('product.free.store.pickup.new')}
            </p>
            <div className={styles.fullfillmentOptionFlexContainer}>
              {isClient && !favouriteStoreDetails?.data?.mec_store_id && (
                <SelectStoreMessage />
              )}

              {isClient &&
                favouriteStoreDetails?.data?.mec_store_id &&
                !selectedVariant && (
                  <SelectVariantMessage
                    colours={colours?.length}
                    sizes={sizes?.length}
                  />
                )}

              {favouriteStoreDetails?.data?.mec_store_id && (
                <StoreAvailability
                  storeId={favouriteStoreDetails?.data?.mec_store_id}
                />
              )}
            </div>

            <InStorePromotion />
          </div>
        </div>
      </div>
      {selectedAvailability && (
        <div className={styles.deliveryFullfillmentOption}>
          <IconWrapper icon="delivery-truck" width={26} height={24} />
          <div className={styles.fullfillmentOptionContent}>
            <div className={styles.fullfillmentOptionContentInnerContainer}>
              <p className={styles.fullfillmentOptionTitle}>
                {t('product.shipping.message.new')}
              </p>
              <div className={styles.fullfillmentOptionFlexContainer}>
                {!selectedVariant && (
                  <SelectVariantMessage
                    colours={colours?.length}
                    sizes={sizes?.length}
                    isAddress={true}
                  />
                )}
                <p className={styles.fullfillmentOptionDescription}>
                  {selectedVariant &&
                    t('product.shipping.message.delivery.time', {
                      deliveryTime: shippingOption?.estimatedDeliveryDate,
                    })}
                </p>
                {selectedVariant && (
                  <ShippingRestrictionMessage
                    shippingRestrictions={shippingRestrictions}
                  />
                )}

                {stockLevelMessage && (
                  <span className={styles.stockLevelContainer}>
                    {stockLevel === 'low' && (
                      <IconWrapper
                        icon="fire"
                        height={20}
                        fill="var(--color-red)"
                        classNames={styles.icon}
                        width={20}
                      />
                    )}
                    <p
                      className={cn(
                        styles.stockLevelMessage,
                        stockLevel ? styles[stockLevel.toString()] : undefined
                      )}
                    >
                      {stockLevelMessage}
                    </p>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductAvailability;
